import React from 'react'
import { ErroredInstance, LoadingInstance, isErrored, isLoading } from '@rwwa/rwwa-data-access'
import { Observable } from 'rx'
import { TypedRecord, recordify } from 'typed-immutable-record'
import { getEditAddressRoute } from '@mobi/account/Areas/ContactDetails/Utils/routes'
import { type HostApi, HostProvider } from '@mobi/account/Common/HostContext'
import { BetAccountHolder } from '@mobi/api-types'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Legacy/Grid'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { isHomeUrl } from '@core/Areas/Navigation/helpers'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import { CloseButton } from '@core/Components/Buttons/CloseButton'
import { appHistory } from '@core/Utils/routing/history'
import { connect } from '../../Components/HOCs/connect'
import { observeImmutable } from '../../Components/HOCs/observe'
import { ErrorMessage } from '../../Components/Messages'
import {
  PopupContainerStyled,
  PopupStyled,
  PopupTitleStyled,
} from '../../Components/Popup/Popup.styles'
import { ContactDetails as ContactDetailsRepo } from '../../Data/Account/contactDetails'
import { state$ as userAccountState$ } from '../../State/UserAccount/userAccountDriver'
import { buildAddress, buildName, isInternationalCustomer } from '../ContactDetails/data-transforms'
import { DueMessage } from './Components/DueMessage'
import { ProvideExtraPersonalIdMessage } from './Components/ProvideExtraPersonalIdMessage'
import { ReverificationComplete } from './Components/ReverificationComplete'
import {
  AdditionalLinksStyled,
  DataStyled,
  DescriptionStyled,
  DetailsListStyled,
  HorizontalDivider,
  NoticeBoxInternationalStyled,
} from './ReverificationPopup.styles'
import {
  ConfirmReverificationDetails,
  RemindMeLater,
  state$ as reverificationState$,
} from './driver'

export interface PopupState {
  hasAttemptedReverification: boolean
  reverificationDue: boolean
  numDaysRemaining: number
  isReverificationSuccess: boolean
  isReverificationFailed: boolean
  isFeatureActive: boolean
  requiresReverification: boolean
  remindMeLater: boolean
  userInitiatedReverification: boolean
  isLoggedIn: boolean
  isProcessing: boolean
  betAccountHolderNumber: number
  isReverificationCompleted: boolean
}

interface PopupStateRecord extends TypedRecord<PopupStateRecord>, PopupState {}

const popupState$: Observable<PopupStateRecord> = Observable.combineLatest(
  reverificationState$,
  userAccountState$,
  (reverification, userAccount) =>
    recordify({
      hasAttemptedReverification: reverification.hasAttemptedReverification,
      reverificationDue: reverification.reverificationDue,
      numDaysRemaining: reverification.numDaysRemaining,
      isReverificationSuccess: reverification.isReverificationSuccess,
      isReverificationFailed: reverification.isReverificationFailed,
      isFeatureActive: reverification.isFeatureActive,
      requiresReverification: reverification.requiresReverification,
      remindMeLater: reverification.remindMeLater,
      userInitiatedReverification: reverification.userInitiatedReverification,
      isLoggedIn: userAccount.isLoggedIn,
      isProcessing: reverification.isLoading,
      betAccountHolderNumber: reverification.betAccountHolderNumber,
      isReverificationCompleted: reverification.isReverificationCompleted,
    })
)

export const ReverificationPopup = (props: { isDesktop: boolean }) =>
  observeImmutable(popupState$, ({ record }) => {
    const {
      hasAttemptedReverification,
      reverificationDue,
      numDaysRemaining,
      isReverificationSuccess,
      isReverificationFailed,
      isProcessing,
      betAccountHolderNumber,
    } = record
    const showPopup = shouldShowPopup(record)

    return (
      <div>
        {isProcessing && <Spinner />}
        <ReverificationPopupComponent
          showPopup={showPopup}
          hasAttemptedReverification={hasAttemptedReverification}
          reverificationDue={reverificationDue}
          numDaysRemaining={numDaysRemaining}
          isReverificationSuccess={isReverificationSuccess}
          isReverificationFailed={isReverificationFailed}
          betAccountHolderNumber={betAccountHolderNumber}
          isDesktop={props.isDesktop}
        />
      </div>
    )
  })

const ReverificationPopupDesktopComponent = ReverificationPopup({ isDesktop: true })

export const ReverificationPopupDesktop = ({ hostApi }: { hostApi: HostApi }) => {
  return (
    <HostProvider api={hostApi}>
      <ReverificationPopupDesktopComponent />
    </HostProvider>
  )
}

export const ReverificationPopupMobi = ReverificationPopup({ isDesktop: false })

export function shouldShowPopup({
  isFeatureActive,
  isLoggedIn,
  requiresReverification,
  remindMeLater,
  userInitiatedReverification,
  betAccountHolderNumber,
}: PopupState) {
  return (
    isFeatureActive &&
    isLoggedIn &&
    requiresReverification &&
    ((isHomeUrl(window.location.pathname) && !remindMeLater) || userInitiatedReverification) &&
    !!betAccountHolderNumber
  )
}

interface ReverificationPopupComponentProps {
  showPopup: boolean
  hasAttemptedReverification: boolean
  reverificationDue: boolean
  numDaysRemaining: number
  isReverificationSuccess: boolean
  isReverificationFailed: boolean
  betAccountHolderNumber: number
  isDesktop: boolean
}

export const ReverificationPopupComponent = ({
  showPopup,
  hasAttemptedReverification,
  reverificationDue,
  numDaysRemaining,
  isReverificationSuccess,
  isReverificationFailed,
  betAccountHolderNumber,
  isDesktop,
}: ReverificationPopupComponentProps) => {
  if (isReverificationSuccess || isReverificationFailed) {
    return <ReverificationComplete isDesktop={isDesktop} />
  }

  if (!showPopup) {
    return null
  }

  return (
    <ReverificationPopupContents
      numDaysRemaining={numDaysRemaining}
      reverificationDue={reverificationDue}
      hasAttemptedReverification={hasAttemptedReverification}
      betAccountHolderNumber={betAccountHolderNumber}
      isDesktop={isDesktop}
    />
  )
}

interface ReverificationPopupContentsProps {
  numDaysRemaining: number
  reverificationDue: boolean
  hasAttemptedReverification: boolean
  betAccountHolderNumber: number
  isDesktop: boolean
}

const ReverificationPopupContents = ({
  numDaysRemaining,
  reverificationDue,
  hasAttemptedReverification,
  betAccountHolderNumber,
  isDesktop,
}: ReverificationPopupContentsProps) => {
  return (
    <PopupContainerStyled>
      <PopupStyled>
        <CloseButton onClick={RemindMeLater} icon={<Icon type='cross' />} />
        <PopupTitleStyled>Account Re-verification</PopupTitleStyled>
        <DueMessage
          hasAttemptedReverification={hasAttemptedReverification}
          numDaysRemaining={numDaysRemaining}
          reverificationDue={reverificationDue}
        />
        {hasAttemptedReverification ? (
          <div>
            <ProvideExtraPersonalIdMessage />
            <ButtonBlock
              onClick={navigateToEditContactDetails({ isDesktop, betAccountHolderNumber })}
              color='primary'
            >
              Provide Extra Personal ID
            </ButtonBlock>
          </div>
        ) : (
          <ReverificationDetails
            isDesktop={isDesktop}
            betAccountHolderNumber={betAccountHolderNumber}
          />
        )}

        <Grid>
          <GridRow>
            <GridCell align='left'>
              <AdditionalLinksStyled
                href='https://help.tabtouch.com.au/knowledge-base/aml-ctf-re-verification-learn-more/'
                target='_blank'
              >
                Learn more
              </AdditionalLinksStyled>
            </GridCell>
            <GridCell align='right'>
              <AdditionalLinksStyled onClick={RemindMeLater}>Remind Me Later</AdditionalLinksStyled>
            </GridCell>
          </GridRow>
        </Grid>
      </PopupStyled>
    </PopupContainerStyled>
  )
}

interface ReverificationDetailsProps {
  fullName?: string
  addressResidential?: string | null
  isInternationalCustomer: boolean
  betAccountHolderNumber: number
  isDesktop: boolean
}

function navigateToEditContactDetails({
  isDesktop,
  betAccountHolderNumber,
}: {
  isDesktop?: boolean
  betAccountHolderNumber: number
}) {
  return () => {
    RemindMeLater()
    if (isDesktop) {
      window.location.href = `/account/mypreferences/contactdetailsedit/${betAccountHolderNumber}`
    } else {
      appHistory.push(getEditAddressRoute(betAccountHolderNumber.toString()))
    }
  }
}

const ReverificationDetails = ({
  betAccountHolderNumber,
  isDesktop,
}: {
  betAccountHolderNumber: number
  isDesktop?: boolean
}) => {
  // @ts-expect-error Legacy usage, needs refactor
  const ConnectReverificationDetails: React.ComponentClass<{
    contactDetails: string
    isDesktop?: boolean
  }> = connect({
    contactDetails: ContactDetailsRepo,
  })(ReverificationDetailsLoader)
  return (
    <ConnectReverificationDetails
      contactDetails={`${betAccountHolderNumber}`}
      isDesktop={isDesktop}
    />
  )
}

const ReverificationDetailsLoader = ({
  contactDetails,
  isDesktop,
}: {
  contactDetails: typeof LoadingInstance | typeof ErroredInstance | BetAccountHolder
  isDesktop: boolean
}) => {
  if (isLoading(contactDetails)) {
    return <Spinner />
  }

  if (isErrored(contactDetails)) {
    return <ErrorMessage>An error occurred while loading your contact details ...</ErrorMessage>
  }

  const props: ReverificationDetailsProps = {
    fullName: contactDetails.NameDetails && buildName(contactDetails.NameDetails),
    addressResidential:
      contactDetails.ResidentialAddress && buildAddress(contactDetails.ResidentialAddress),
    betAccountHolderNumber: contactDetails.BetAccountHolderNumber,
    isInternationalCustomer: isInternationalCustomer(contactDetails.ResidentialAddress),
    isDesktop: isDesktop,
  }

  return <ReverificationDetailsView {...props} />
}

export const ReverificationDetailsView = ({
  fullName,
  addressResidential,
  betAccountHolderNumber,
  isInternationalCustomer: isInternational,
  isDesktop,
}: ReverificationDetailsProps) => {
  const confirm = () => ConfirmReverificationDetails({ betAccountHolderNumber })

  return (
    <div>
      <DetailsListStyled>
        <DescriptionStyled>Full Name</DescriptionStyled>
        <DataStyled>{fullName}</DataStyled>

        <HorizontalDivider />

        <DescriptionStyled>Address - Residential</DescriptionStyled>
        <DataStyled>{addressResidential}</DataStyled>
      </DetailsListStyled>

      <Grid padding='0.4rem'>
        <GridRow>
          <GridCell>
            <ButtonBlock onClick={confirm} color='primary' uppercaseText={true}>
              Confirm
            </ButtonBlock>
          </GridCell>
        </GridRow>

        <GridRow>
          <GridCell>
            {isInternational ? (
              <NoticeBoxInternationalStyled>
                If your name, address or date of birth are incorrect, please call us on{' '}
                <CustomerServiceTelephone isInternational={true} /> for assistance.
              </NoticeBoxInternationalStyled>
            ) : (
              <ButtonBlock
                onClick={navigateToEditContactDetails({ isDesktop, betAccountHolderNumber })}
                color='secondary'
              >
                No, I need to update my address details
              </ButtonBlock>
            )}
          </GridCell>
        </GridRow>
      </Grid>
    </div>
  )
}
